import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useQueryReportSecondaryCategory } from '../../models/useQueryReportSecondaryCategory'

export interface IUseReportSecondaryCategoryPresenterParams {
  /** Route param — the FS/Page owns routing context (ADR-9) */
  secondaryCategoryId: number
  /** False when another report config's header hook should fetch instead */
  enabled?: boolean
  /**
   * Toasts a non-404 read failure. Every observer of a react-query v3 query
   * runs its own onError, so only one caller of the shared header query
   * (the detail Feature Set) opts in.
   */
  notifyOnError?: boolean
}

/**
 * Hook-only presenter (ADR-2): resolves the secondary category for BOTH
 * the orders and expenses detail pages. The breadcrumb shows `name`; the
 * detail heading also shows the primary category it belongs to.
 */
export const useReportSecondaryCategoryPresenter = ({
  secondaryCategoryId,
  enabled = true,
  notifyOnError = false,
}: IUseReportSecondaryCategoryPresenterParams) => {
  const intl = useProcurifyIntl()

  // The model reports an unknown id through `isNotFound` and passes only
  // other failures here.
  const handleError = (error: unknown) => {
    toast.error({
      title: intl.formatMessage(messages.errorToastTitle),
      children:
        getMessageFromError(error) ??
        intl.formatMessage(messages.loadErrorFallbackMessage),
    })
  }

  const { secondaryCategory, isLoading, isNotFound } =
    useQueryReportSecondaryCategory(secondaryCategoryId, enabled, {
      onError: notifyOnError ? handleError : undefined,
    })

  return {
    data: {
      secondaryCategoryName: secondaryCategory?.name ?? null,
      primaryCategoryName: secondaryCategory?.primaryCategory?.name ?? null,
    },
    states: {
      isLoading,
      isNotFound,
    },
  }
}
